/**
 * @fileoverview Base Modal Component
 *
 * Reusable modal component with overlay, animations, and flexible content.
 * Provides a consistent foundation for all modal implementations across the app.
 *
 * Features:
 * - Backdrop click to close
 * - Escape key support
 * - Focus management
 * - Smooth animations
 * - Flexible sizing options
 * - Accessibility support
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 */

import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FiX } from 'react-icons/fi';
import './Modal.css';

/**
 * Base Modal Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function called when modal should close
 * @param {ReactNode} props.children - Modal content
 * @param {string} props.size - Modal size: 'sm', 'md', 'lg', 'xl'
 * @param {boolean} props.closeOnBackdrop - Whether clicking backdrop closes modal
 * @param {boolean} props.closeOnEscape - Whether escape key closes modal
 * @param {boolean} props.showCloseButton - Whether to show the close button
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.preventScroll - Whether to prevent body scroll when open
 */
const Modal = ({
  isOpen = false,
  onClose,
  children,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = '',
  preventScroll = true
}) => {
  // Handle escape key
  const handleEscape = useCallback((event) => {
    if (closeOnEscape && event.key === 'Escape' && onClose) {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((event) => {
    if (closeOnBackdrop && event.target === event.currentTarget && onClose) {
      onClose();
    }
  }, [closeOnBackdrop, onClose]);

  // Handle body scroll prevention
  useEffect(() => {
    if (!preventScroll) return;

    if (isOpen) {
      // Prevent body scroll
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen, preventScroll]);

  // Add escape key listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, handleEscape]);

  // Don't render if not open
  if (!isOpen) return null;

  const modalSizeClass = `modal-${size}`;
  const modalClasses = `modal-container ${modalSizeClass} ${className}`.trim();

  return (
    <div
      className="modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className={modalClasses}>
        {showCloseButton && (
          <button
            className="modal-close-button"
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            <FiX />
          </button>
        )}
        {children}
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  children: PropTypes.node,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  closeOnBackdrop: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
  preventScroll: PropTypes.bool
};

Modal.defaultProps = {
  isOpen: false,
  onClose: null,
  children: null,
  size: 'md',
  closeOnBackdrop: true,
  closeOnEscape: true,
  showCloseButton: true,
  className: '',
  preventScroll: true
};

export default Modal;